import UserValidationModel from './userValidation'
import { SetupGuideEnum } from '../../enums/modules/routes'
import { ObjectWithDefault } from '../../utils/objectWithDefault'

// NOTE: If there is any data missing here that you might need
//  we are making a call to detailedUserData for settings.
//  Look on that object to see if the data you need is there.
export default class UserModel extends UserValidationModel {
  constructor (u) {
    super()
    const defaultUser = {
      UserId: { val: 0, type: Number },
      ImpersonatedUserId: { val: 0, type: Number },
      UserRoleId: { val: 0, type: Number },
      CityID: { val: 0, type: Number },
      FirstName: { val: '', type: String },
      LastName: { val: '', type: String },
      Email: { val: '', type: String },
      PhoneNumber: { val: '', type: String },
      CompanyName: { val: '', type: String },
      OrganizationId: { val: '', type: String },
      CompanyWebsite: { val: '', type: String },
      WorkingWithSales: { val: false, type: Boolean },
      TotalOrdersPerMonth: { val: '', type: String },
      CustomLeadSource: { val: 'New Signup', type: String },
      password: { val: '', type: String },
      confirmPassword: { val: '', type: String },
      verifiedRecaptcha: { val: false, type: Boolean },
      employeeSignupToken: { val: '', type: String },
      preLaunchUser: { val: false, type: Boolean },
      eCommercePlatform: { val: '', type: String },
      emailOptIn: { val: true, type: Boolean },
      LastCompletedStep: { val: 8, type: Number },
      IsAdmin: { val: false, type: Boolean },
      IsOnboarding: { val: false, type: Boolean },
      ImpersonatedEmail: { val: '', type: String },
      ImpersonatedRoleId: { val: 0, type: Number },
      ImpersonatedCompanyName: { val: '', type: String },
      defaultStoreIntegrationId: { val: -1, type: Number },
      utmSource: { val: '', type: String }
    }
    const user = ObjectWithDefault(defaultUser, u)
    this.id = user.UserId
    this.impersonatedUserId = user.ImpersonatedUserId
    this.role = user.UserRoleId
    this.cityId = user.CityID
    this._firstName = user.FirstName
    this._lastName = user.LastName
    this._email = user.Email
    this._phoneNumber = user.PhoneNumber
    this._companyName = user.CompanyName
    this._companyWebsite = user.CompanyWebsite
    this._workingWithSales = user.WorkingWithSales
    this._totalOrdersPerMonth = user.TotalOrdersPerMonth
    this._customLeadSource = user.CustomLeadSource
    this._password = user.password
    this._confirmPassword = user.confirmPassword
    this._eCommercePlatform = user.eCommercePlatform
    this._emailOptIn = user.emailOptIn
    this.verifiedRecaptcha = user.verifiedRecaptcha
    this.preLaunchUser = user.preLaunchUser
    this.employeeSignupToken = user.employeeSignupToken
    this.lastCompletedStep = user.LastCompletedStep
    this.isAdmin = user.IsAdmin
    this.isOnboarding = user.IsOnboarding
    this.impersonatedEmail = user.ImpersonatedEmail
    this.impersonatedRoleId = user.ImpersonatedRoleId
    this.impersonatedCompanyName = user.ImpersonatedCompanyName
    this.defaultStoreIntegrationId = user.defaultStoreIntegrationId
    this.utmSource = user.utmSource
  }

  get firstName () {
    return this.mapAttributeGetter('firstName')
  }

  set firstName (val) {
    this.mapAttributeSetter('firstName', val)
  }

  get lastName () {
    return this.mapAttributeGetter('lastName')
  }

  set lastName (val) {
    this.mapAttributeSetter('lastName', val)
  }

  get companyName () {
    return this.mapAttributeGetter('companyName')
  }

  set companyName (val) {
    this.mapAttributeSetter('companyName', val)
  }

  get companyWebsite () {
    return this.mapAttributeGetter('companyWebsite')
  }

  set companyWebsite (val) {
    this.mapAttributeSetter('companyWebsite', val)
  }

  get workingWithSales () {
    return this.mapAttributeGetter('workingWithSales')
  }

  set workingWithSales (val) {
    this.mapAttributeSetter('workingWithSales', val)
  }

  get email () {
    return this.mapAttributeGetter('email')
  }

  set email (val) {
    this.mapAttributeSetter('email', val)
  }

  get phoneNumber () {
    return this.mapAttributeGetter('phoneNumber')
  }

  set phoneNumber (val) {
    this.mapAttributeSetter('phoneNumber', val)
  }

  get totalOrdersPerMonth () {
    return this.mapAttributeGetter('totalOrdersPerMonth')
  }

  set totalOrdersPerMonth (val) {
    this.mapAttributeSetter('totalOrdersPerMonth', val)
  }

  get customLeadSource () {
    return this.mapAttributeGetter('customLeadSource')
  }

  set customLeadSource (val) {
    this.mapAttributeSetter('customLeadSource', val)
  }

  get password () {
    return this.mapAttributeGetter('password')
  }

  set password (val) {
    this.mapAttributeSetter('password', val)
  }

  get confirmPassword () {
    return this.mapAttributeGetter('confirmPassword', { matchValue: this._password })
  }

  set confirmPassword (val) {
    this.mapAttributeSetter('confirmPassword', val)
  }

  get eCommercePlatform () {
    return this.mapAttributeGetter('eCommercePlatform')
  }

  set eCommercePlatform (val) {
    this.mapAttributeSetter('eCommercePlatform', val)
  }

  get emailOptIn () {
    return this.mapAttributeGetter('emailOptIn')
  }

  set emailOptIn (val) {
    this.mapAttributeSetter('emailOptIn', val)
  }

  get utmSource () {
    return this.mapAttributeGetter('utmSource')
  }

  set utmSource (val) {
    this.mapAttributeSetter('utmSource', val)
  }

  isDefault () {
    return this.id === 0
  }

  onboardingStatus () {
    const step = this.getStep(this.lastCompletedStep)
    const isOnboarding = step !== SetupGuideEnum.Completed && step !== SetupGuideEnum.Login
    return { isOnboarding, step }
  }

  stepMap () {
    return {
      0: SetupGuideEnum.Completed,
      1: SetupGuideEnum.SignUp,
      2: SetupGuideEnum.About,
      3: SetupGuideEnum.Phone,
      4: SetupGuideEnum.FinalStep,
      5: SetupGuideEnum.PhoneConfirm,
      6: SetupGuideEnum.Booking,
      7: SetupGuideEnum.Business,
      8: SetupGuideEnum.Login,
      9: SetupGuideEnum.FreeAccount,
      10: SetupGuideEnum.LaunchDate,
      11: SetupGuideEnum.OnboardingComplete,
      12: SetupGuideEnum.CompletedQuoteGenerator,
      13: SetupGuideEnum.CertifyFulfillmentSettings
    }
  }

  getStep (step) {
    return this.stepMap()[step]
  }

  getStepEnum (step) {
    return parseInt(Object.entries(this.stepMap()).find(e => e[1] === step)[0])
  }

  mapUserForRegister () {
    return {
      firstName: this._firstName,
      lastName: this._lastName,
      email: this._email,
      password: this._password,
      phoneNumber: this._phoneNumber,
      companyName: this._companyName,
      companyWebsite: this._companyWebsite,
      totalOrdersPerMonth: this._totalOrdersPerMonth,
      ecommercePlatform: this._eCommercePlatform,
      customLeadSource: this._customLeadSource,
      isAlreadyWorkingWithSales: this._workingWithSales,
      employeeSignupToken: this.employeeSignupToken,
      emailOptIn: this._emailOptIn,
      utmSource: this._utmSource
    }
  }

  mapForAnalytics () {
    return {
      id: this.id,
      email: this._email,
      firstName: this._firstName,
      lastName: this._lastName
    }
  }

  mapEmailForForgotPassword (args) {
    return {
      email: this._email,
      ...args
    }
  }

  mapForLogin () {
    return {
      email: this._email,
      password: this._password
    }
  }

  mapForNewPassword () {
    return {
      email: this._email,
      id: this.employeeSignupToken,
      password: this._password
    }
  }

  mapForAdminPage () {
    return {
      impersonatedEmail: this.impersonatedEmail,
      impersonatedUserId: this.impersonatedUserId
    }
  }
}
